import { motion } from 'motion/react';
import { KeyboardEvent, ReactNode, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { MAX_NAME_LENGTH } from '~/consts';
import { BottomSheet } from '~/modules/elements/akui/bottom-sheet';
import { Icon, IconName } from '~/modules/elements/akui/icon';
import events from '~/modules/game-events/game-events';
import { useEventListener } from '~/modules/game-events/hooks';
import { profileInitial } from '~/modules/user-profiles/profiles';
import { HelpEntry } from '~/routes/keyboard-help/context';
import { ControlDescriptor, ControlOption, renderableControls } from '~/routes/keyboard-help/controls';
import { checkSingerName } from '~/routes/remote-mic/components/name-rule';
import { usePhoneToast } from '~/routes/remote-mic/components/phone-toast-context';
import { playerColour, playerColourAlpha, playerInk } from '~/routes/remote-mic/components/player-colour';
import useRemoteMicId from '~/routes/remote-mic/hooks/use-remote-mic-id';
import {
  buildSlots,
  deviceLabel,
  micOptionView,
  Press,
  singerPress,
  Slot,
  TV_NEW_SINGER,
} from '~/routes/remote-mic/panels/microphone/mic-setup-slots';
import { activateControl, setControlValue } from '~/routes/remote-mic/panels/microphone/remote-actions';
import RemoteControl from '~/routes/remote-mic/panels/microphone/remote-controls';
import { screenTransition } from '~/routes/remote-mic/panels/microphone/screen-transition';

type Select = Extract<ControlDescriptor, { type: 'select' }>;

const BUILT_IN = 'use-built-in';

const KIND_ICONS: Record<NonNullable<ControlOption['kind']>, IconName> = {
  phone: 'ic:baseline-smartphone',
  computer: 'ic:baseline-laptop',
  mic: 'ic:baseline-mic',
};

const optionKindIcon = (option: ControlOption | undefined) => KIND_ICONS[option?.kind ?? 'mic'];

const control = (controls: ControlDescriptor[], name: string) => controls.find((candidate) => candidate.name === name);

const act = (press: Press, controlName: string, flash: (message: string) => void) => {
  if (press.kind === 'toast') flash(press.message);
  if (press.kind === 'send') setControlValue(controlName, press.value);
};

// Setup microphones on the phone: the slots as tiles, each opening one sheet that holds its
// microphone, its singer, and the way to clear it.
export default function MicSetupScreen({ keyboard }: { keyboard: HelpEntry }) {
  const controls = renderableControls(keyboard.controls ?? []);
  const [playerNumber] = useEventListener(events.remoteMicPlayerSet, true) ?? [null];
  const flash = usePhoneToast();
  const [openSlot, setOpenSlot] = useState<number | null>(null);

  const slots = buildSlots(controls);
  // Your own slot first: one layout goes to every phone, so only the phone knows which one is its own.
  slots.sort((left, right) => Number(right.player === playerNumber) - Number(left.player === playerNumber));

  const done = control(controls, keyboard.primary ?? 'back-button');
  const builtIn = control(controls, BUILT_IN);
  const grouped = controls.filter((candidate) => candidate.group !== undefined);
  const leftovers = controls.filter(
    (candidate) => candidate !== done && candidate !== builtIn && !grouped.includes(candidate),
  );

  const filledCount = slots.filter((slot) => slot.filled).length;
  const emptySlots = slots.filter((slot) => !slot.filled);
  const builtInWaits = emptySlots.length > 0 && emptySlots.every((slot) => slot.waitsForSong);
  const sheetSlot = slots.find((slot) => slot.player === openSlot);

  const openTile = (slot: Slot) => {
    if (slot.waitsForSong) {
      flash(`${slot.label} can join once this song ends`);
      return;
    }
    setOpenSlot(slot.player);
  };

  return (
    <motion.div
      data-test="remote-keyboard"
      data-mode="mirror"
      data-screen="mic-setup"
      className="flex h-full min-h-0 w-full flex-col gap-3.5 pb-0.5"
      {...screenTransition}>
      <div data-test="remote-keyboard-header" className="flex shrink-0 flex-col gap-0.5">
        <div className="flex items-center gap-2.5">
          <Icon icon="ic:baseline-mic-external-on" size={5.5} className="shrink-0 text-white/60" />
          <span className="min-w-0 flex-1 truncate text-[1.375rem] font-bold">{keyboard.title}</span>
        </div>
        <span data-test="remote-keyboard-subtitle" className="text-active truncate pl-8 text-[0.9375rem]">
          {keyboard.subtitle ?? `${filledCount} of ${slots.length} players ready`}
        </span>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto overscroll-contain">
        <div className="grid shrink-0 grid-cols-2 gap-2.5">
          {slots.map((slot) => (
            <SlotTile
              key={slot.group.id}
              slot={slot}
              isYou={slot.player === playerNumber}
              compact={slots.length > 4}
              onOpen={openTile}
            />
          ))}
        </div>

        {builtIn && emptySlots.length > 0 && (
          <button
            type="button"
            onClick={() =>
              builtInWaits ? flash('New players can join once this song ends') : activateControl(builtIn.name)
            }
            disabled={builtIn.disabled}
            data-test={`control-${builtIn.name}`}
            data-control-type="button"
            className={twMerge(
              'flex h-14 shrink-0 cursor-pointer items-center gap-3 rounded-2xl border-none bg-transparent px-4 text-left text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)] transition-transform duration-100 active:scale-[0.98] disabled:opacity-40',
              builtInWaits && 'opacity-50',
            )}>
            <Icon icon="ic:baseline-tv" size={5.5} className="shrink-0 text-white/70" />
            <span className="min-w-0 flex-1 truncate text-[1rem]">{builtIn.label}</span>
            <Icon icon="ic:baseline-arrow-forward" size={5} className="shrink-0 opacity-70" />
          </button>
        )}

        {leftovers.map((candidate) => (
          <RemoteControl key={candidate.name} control={candidate} onActivate={activateControl} />
        ))}
      </div>

      {done && (
        <button
          type="button"
          onClick={() => activateControl(done.name)}
          disabled={done.disabled}
          data-test={`control-${done.name}`}
          data-control-type="button"
          className="bg-active flex h-16 shrink-0 cursor-pointer items-center justify-center rounded-[1.125rem] border-none text-[1.1875rem] font-bold text-black transition-transform duration-100 active:scale-[0.98] disabled:opacity-40">
          {done.label}
        </button>
      )}

      <SlotSheet slot={sheetSlot} isYou={sheetSlot?.player === playerNumber} onClose={() => setOpenSlot(null)} />
    </motion.div>
  );
}

function SlotTile({
  slot,
  isYou,
  compact,
  onOpen,
}: {
  slot: Slot;
  isYou: boolean;
  // Past four slots the tiles shrink, so eight of them take little more than a screen.
  compact: boolean;
  onOpen: (slot: Slot) => void;
}) {
  const avatar = compact ? 'size-11' : 'size-15';
  const colour = playerColour(slot.player);
  const ink = playerInk(slot.player);
  const micOnly = slot.filled && !slot.singer;
  const noSinger = slot.filled && !!slot.singer && !slot.singerName;

  return (
    <button
      type="button"
      onClick={() => onOpen(slot)}
      data-test={`mic-slot-${slot.player}`}
      data-filled={slot.filled}
      data-you={isYou}
      data-singer={slot.singerName ?? ''}
      className={`relative flex min-w-0 cursor-pointer flex-col items-center rounded-[1.25rem] border-none text-center text-white transition-transform duration-100 active:scale-[0.98] ${
        compact ? 'h-40 p-3' : 'h-52.5 p-3.5'
      }`}
      style={{
        background: slot.filled ? playerColourAlpha(slot.player, 0.16) : 'rgba(255,255,255,.03)',
        boxShadow: isYou
          ? `inset 0 0 0 3px ${colour}`
          : slot.filled
            ? `inset 0 0 0 1px ${playerColourAlpha(slot.player, 0.7)}`
            : 'inset 0 0 0 1.5px rgba(255,255,255,.18)',
        opacity: slot.waitsForSong ? 0.5 : 1,
      }}>
      <span className="flex items-center justify-between gap-1.5 self-stretch">
        <span className="text-[0.8125rem] font-bold tracking-[0.08em] uppercase" style={{ color: colour }}>
          {slot.label}
        </span>
        {isYou && (
          <span className="bg-active rounded px-[0.3125rem] py-0.5 text-[0.6875rem] font-black text-black">YOU</span>
        )}
      </span>

      {!slot.filled && (
        <span className="flex flex-1 flex-col items-center justify-center gap-2.5">
          <span
            className={`flex items-center justify-center rounded-full shadow-[inset_0_0_0_2px_rgba(255,255,255,0.3)] ${avatar}`}>
            <Icon icon={slot.waitsForSong ? 'ic:baseline-schedule' : 'ic:baseline-add'} size={compact ? 6 : 7} />
          </span>
          <span className="text-[0.9375rem] font-bold">{slot.waitsForSong ? 'After this song' : 'Add player'}</span>
        </span>
      )}

      {micOnly && (
        <span className="flex w-full min-w-0 flex-1 flex-col items-center justify-center gap-2">
          <span
            className={`flex items-center justify-center rounded-full ${avatar}`}
            style={{ background: colour, color: ink }}>
            <Icon icon={optionKindIcon(slot.device)} size={compact ? 6 : 7} />
          </span>
          <span className="ph-no-capture max-w-full truncate text-[1.0625rem] font-bold">
            {slot.device ? deviceLabel(slot.device) : slot.label}
          </span>
        </span>
      )}

      {slot.filled && !micOnly && (
        <>
          <span className="flex w-full min-w-0 flex-1 flex-col items-center justify-center gap-2">
            <span
              className={`flex items-center justify-center rounded-full font-bold ${avatar} ${
                compact ? 'text-[1.25rem]' : 'text-[1.5rem]'
              }`}
              style={
                noSinger
                  ? { color: 'rgb(255,165,0)', boxShadow: 'inset 0 0 0 2px rgb(255,165,0)' }
                  : { background: colour, color: ink }
              }>
              {noSinger ? '?' : profileInitial(slot.singerName ?? '')}
            </span>
            <span
              className={twMerge(
                'ph-no-capture max-w-full truncate text-[1.0625rem] font-bold',
                noSinger && 'text-active',
              )}>
              {noSinger ? 'Pick a singer' : slot.singerName}
            </span>
          </span>
          <span className="flex h-8 min-w-0 items-center justify-center gap-1.5 self-stretch rounded-[0.625rem] bg-black/35 px-2 text-[0.8125rem] text-white/85">
            <Icon icon={optionKindIcon(slot.device)} size={4} className="shrink-0" />
            <span className="ph-no-capture truncate">{slot.device ? deviceLabel(slot.device) : 'No microphone'}</span>
          </span>
        </>
      )}
    </button>
  );
}

function SlotSheet({ slot, isYou, onClose }: { slot: Slot | undefined; isYou: boolean; onClose: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const flash = usePhoneToast();
  const myMicId = useRemoteMicId();

  const close = () => {
    setConfirming(false);
    setAdding(false);
    setDraft('');
    onClose();
  };

  const colour = slot ? playerColour(slot.player) : 'transparent';
  const title = !slot ? '' : confirming ? `Stop being ${slot.label}?` : slot.filled ? slot.label : `Add ${slot.label}`;

  const leave = () => {
    if (!slot?.remove) return;
    activateControl(slot.remove.name);
    close();
    flash(isYou ? 'This phone left the slot' : `${slot.label} cleared`);
  };

  return (
    <BottomSheet
      open={!!slot}
      onClose={close}
      title={
        <span className="flex min-w-0 items-center gap-2.5" data-test="slot-sheet-title">
          <span aria-hidden className="size-3 shrink-0 rounded-full" style={{ background: colour }} />
          <span className="min-w-0 truncate">{title}</span>
        </span>
      }>
      {slot && confirming && (
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={leave}
            data-test="control-confirm"
            className="flex h-16 cursor-pointer items-center justify-center rounded-[1.125rem] border-none bg-black/50 text-[1.1875rem] font-bold text-red-400 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)] transition-transform duration-100 active:scale-[0.98]">
            Leave the slot
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            data-test="control-confirm-cancel"
            className="bg-active flex h-16 cursor-pointer items-center justify-center rounded-[1.125rem] border-none text-[1.1875rem] font-bold text-black transition-transform duration-100 active:scale-[0.98]">
            Stay
          </button>
        </div>
      )}

      {slot && !confirming && (
        <div className="flex flex-col gap-4.5">
          <SheetSection label="Microphone">
            <MicrophoneOptions slot={slot} myMicId={myMicId} />
          </SheetSection>

          {slot.filled && slot.singer && (
            <SheetSection label="Who's singing?">
              {adding ? (
                <NewSingerForm
                  slot={slot}
                  singer={slot.singer}
                  draft={draft}
                  setDraft={setDraft}
                  onDone={() => {
                    setAdding(false);
                    setDraft('');
                  }}
                />
              ) : (
                <SingerGrid
                  slot={slot}
                  singer={slot.singer}
                  onNew={() => {
                    if (slot.newSinger) {
                      setAdding(true);
                    } else {
                      setControlValue(slot.singer!.name, TV_NEW_SINGER);
                      close();
                    }
                  }}
                />
              )}
            </SheetSection>
          )}

          <div className="flex gap-2.5">
            {slot.remove && (
              <button
                type="button"
                onClick={() => (isYou ? setConfirming(true) : leave())}
                disabled={slot.remove.disabled}
                data-test="slot-remove"
                className="flex h-15 flex-1 cursor-pointer items-center justify-center rounded-[1.125rem] border-none bg-white/8 text-[1rem] font-bold text-red-400 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)] transition-transform duration-100 active:scale-[0.98] disabled:opacity-40">
                {isYou ? 'Leave slot' : 'Clear slot'}
              </button>
            )}
            <button
              type="button"
              onClick={close}
              data-test="slot-done"
              className="bg-active flex h-15 flex-1 cursor-pointer items-center justify-center rounded-[1.125rem] border-none text-[1.125rem] font-bold text-black transition-transform duration-100 active:scale-[0.98]">
              Done
            </button>
          </div>
        </div>
      )}
    </BottomSheet>
  );
}

function SheetSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2.5">
      <span className="text-[0.8125rem] tracking-[0.08em] text-white/70 uppercase">{label}</span>
      {children}
    </div>
  );
}

function MicrophoneOptions({ slot, myMicId }: { slot: Slot; myMicId: string | null }) {
  const flash = usePhoneToast();
  const options = slot.input?.options ?? [];

  if (options.length === 0) {
    return slot.add && !slot.add.disabled ? (
      <button
        type="button"
        onClick={() => activateControl(slot.add!.name)}
        data-test="mic-option-add"
        className="flex min-h-14 cursor-pointer items-center gap-3 rounded-2xl border-none bg-white/6 px-4 text-left text-[1rem] font-bold text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)]">
        <Icon icon="ic:baseline-add" size={5.5} className="shrink-0" />
        {slot.add.label}
      </button>
    ) : (
      <span className="text-[0.9375rem] text-white/70">No microphones yet.</span>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {options.map((option, index) => {
        const view = micOptionView(slot, option, myMicId);
        const pick = () => slot.input && act(view.press, slot.input.name, flash);

        return (
          <button
            key={option.value}
            type="button"
            onClick={pick}
            data-test={`mic-option-${view.thisPhone ? 'this-phone' : index}`}
            data-selected={view.selected}
            data-taken={view.taken}
            className={twMerge(
              'flex min-h-14 cursor-pointer items-center gap-3 rounded-2xl border-none px-4 text-left text-[1rem] transition-colors duration-150',
              view.selected
                ? 'bg-active text-black'
                : 'bg-white/6 text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)]',
              view.taken && 'opacity-45',
            )}>
            <Icon icon={view.taken ? 'ic:outline-lock' : optionKindIcon(option)} size={5.5} className="shrink-0" />
            <span className="ph-no-capture min-w-0 flex-1 truncate font-bold">{view.label}</span>
            {view.note && <span className="shrink-0 text-[0.875rem] opacity-75">{view.note}</span>}
          </button>
        );
      })}
    </div>
  );
}

function SingerGrid({ slot, singer, onNew }: { slot: Slot; singer: Select; onNew: () => void }) {
  const flash = usePhoneToast();
  const colour = playerColour(slot.player);
  const options = singer.options.filter((option) => option.value !== TV_NEW_SINGER);
  const fromPhone = !!singer.disabled;

  return (
    <div className="grid grid-cols-3 gap-x-2.5 gap-y-4 pt-1">
      {!fromPhone && (
        <button
          type="button"
          onClick={onNew}
          data-test="slot-new-singer"
          className="flex cursor-pointer flex-col items-center gap-2 border-none bg-transparent p-0 text-white">
          <span className="flex size-16 items-center justify-center rounded-full shadow-[inset_0_0_0_2px_rgba(255,255,255,0.35)]">
            <Icon icon="ic:baseline-add" size={7} />
          </span>
          <span className="flex flex-col items-center">
            <span className="text-[0.875rem] font-bold">New</span>
            <span className="text-[0.75rem] text-white/65">Add a singer</span>
          </span>
        </button>
      )}
      {options.map((option) => {
        const selected = option.value === singer.value;
        const taken = !!option.disabled;
        const pick = () => act(singerPress(slot, option), singer.name, flash);

        return (
          <button
            key={option.value}
            type="button"
            onClick={pick}
            data-test={`slot-singer-${option.label}`}
            data-selected={selected}
            data-taken={taken}
            className="flex min-w-0 cursor-pointer flex-col items-center gap-2 border-none bg-transparent p-0 text-white"
            style={{ opacity: taken || (fromPhone && !selected) ? 0.5 : 1 }}>
            <span
              className="relative flex size-16 items-center justify-center rounded-full text-[1.625rem] font-bold"
              style={
                selected
                  ? {
                      background: colour,
                      color: playerInk(slot.player),
                      boxShadow: `0 0 0 3px #05070b, 0 0 0 6px ${colour}`,
                    }
                  : { background: 'rgba(255,255,255,.12)' }
              }>
              {profileInitial(option.label)}
              {taken && (
                <span className="absolute -right-0.5 -bottom-0.5 flex size-6 items-center justify-center rounded-full bg-[#05070b] text-white">
                  <Icon icon="ic:outline-lock" size={3.5} />
                </span>
              )}
            </span>
            <span className="flex w-full min-w-0 flex-col items-center">
              <span className="ph-no-capture max-w-full truncate text-[0.875rem] font-bold">{option.label}</span>
              <span className="text-[0.75rem] text-white/65">{taken ? option.note : selected ? 'Selected' : ''}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function NewSingerForm({
  slot,
  singer,
  draft,
  setDraft,
  onDone,
}: {
  slot: Slot;
  singer: Select;
  draft: string;
  setDraft: (draft: string) => void;
  onDone: () => void;
}) {
  const flash = usePhoneToast();
  const known = singer.options
    .filter((option) => option.value !== TV_NEW_SINGER)
    .map((option) => ({ name: option.label, taken: !!option.disabled, option }));
  const check = checkSingerName(draft, known);

  const submit = () => {
    if (check.status === 'existing') {
      setControlValue(singer.name, check.match.option.value);
      flash(`${check.match.name} is ${slot.label}`);
      onDone();
    } else if (check.status === 'new' && slot.newSinger) {
      setControlValue(slot.newSinger.name, check.name);
      flash(`Added ${check.name} as ${slot.label}`);
      onDone();
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') submit();
    if (event.key === 'Escape') onDone();
  };

  const ready = check.status === 'existing' || check.status === 'new';
  const label =
    check.status === 'empty'
      ? 'Type a name'
      : check.status === 'taken'
        ? 'Name taken'
        : check.status === 'existing'
          ? `Use ${check.match.name}`
          : `Add ${check.name} as ${slot.label}`;

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex h-15 items-center gap-2.5 rounded-2xl bg-white/8 pr-2 pl-4 shadow-[inset_0_0_0_2px_#facc15]">
        <input
          type="text"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
          maxLength={MAX_NAME_LENGTH}
          placeholder="New singer's name"
          autoFocus
          data-test="slot-new-singer-input"
          className="ph-no-capture h-full min-w-0 flex-1 border-none bg-transparent text-[1.25rem] font-bold text-white outline-none placeholder:text-white/45"
        />
        <button
          type="button"
          aria-label="Back to the singers"
          onClick={onDone}
          className="flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-xl border-none bg-transparent text-white">
          <Icon icon="ic:baseline-close" size={5.5} />
        </button>
      </div>
      <button
        type="button"
        onClick={submit}
        disabled={!ready}
        data-test="slot-new-singer-submit"
        className={twMerge(
          'ph-no-capture flex h-13 cursor-pointer items-center justify-center rounded-2xl border-none px-4 text-[1rem] font-bold transition-colors duration-150',
          ready ? 'bg-active text-black' : 'bg-white/10 text-white/60',
        )}>
        <span className="truncate">{label}</span>
      </button>
      {/* Held open whether or not there is a notice, so the button above never moves under a thumb. */}
      <div className="flex min-h-16 items-start">
        {(check.status === 'existing' || check.status === 'taken') && (
          <div
            data-test="singer-name-clash"
            className="flex flex-1 items-center gap-3 rounded-[0.875rem] bg-[rgba(255,165,0,0.14)] px-3.5 py-3 shadow-[inset_0_0_0_1px_rgba(255,165,0,0.5)]">
            <Icon icon="ic:baseline-info" size={5.5} className="text-active shrink-0" />
            <span className="text-[0.875rem] leading-[1.4]">
              {check.status === 'taken'
                ? `${check.match.name} is already ${check.match.option.note}. Pick a different name.`
                : 'That name is already a singer here. Pick them from the list so the scores land in one place.'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
